//#region Actions

import {AccountInformation} from '../api/userService/entities';
import UserService from '../api/userService/userService';
import LocalStorageService from '../localStorageService';

import {Thunk} from './redux-thunk';
import * as SalesDemosStore from './salesDemosStore';

enum ActionType {
  AuthenticationStart = 'AuthenticationStart',
  AuthenticationWithApiKeyStart = 'AuthenticationWithApiKeyStart',
  AuthenticationSuccess = 'AuthenticationSuccess',
  AuthenticationError = 'AuthenticationError',
  AuthenticationWithApiKeyError = 'AuthenticationWithApiKeyError',
  AuthenticationLogout = 'AuthenticationLogout'
}

const buildAuthenticationStartAction = () => ({
  type: ActionType.AuthenticationStart as typeof ActionType.AuthenticationStart
});

const buildAuthenticationWithApiKeyStartAction = () => ({
  type: ActionType.AuthenticationWithApiKeyStart as typeof ActionType.AuthenticationWithApiKeyStart
});

const buildAuthenticationSuccessAction = (authenticatedAccount: AuthenticatedAccount) => ({
  type: ActionType.AuthenticationSuccess as typeof ActionType.AuthenticationSuccess,
  authenticatedAccount
});

const buildAuthenticationErrorAction = () => ({
  type: ActionType.AuthenticationError as typeof ActionType.AuthenticationError
});

const buildAuthenticationWithApiKeyErrorAction = () => ({
  type: ActionType.AuthenticationWithApiKeyError as typeof ActionType.AuthenticationWithApiKeyError
});

const buildAuthenticationLogoutAction = () => ({
  type: ActionType.AuthenticationLogout as typeof ActionType.AuthenticationLogout
});

type Action =
  | ReturnType<typeof buildAuthenticationStartAction>
  | ReturnType<typeof buildAuthenticationWithApiKeyStartAction>
  | ReturnType<typeof buildAuthenticationSuccessAction>
  | ReturnType<typeof buildAuthenticationErrorAction>
  | ReturnType<typeof buildAuthenticationWithApiKeyErrorAction>
  | ReturnType<typeof buildAuthenticationLogoutAction>;

export const login = (username: string, password: string): Thunk => async dispatch => {
  dispatch(buildAuthenticationStartAction());

  try {
    const accountInformation: AccountInformation = await UserService.login(username, password);

    const authenticatedAccount: AuthenticatedAccount = {
      id: accountInformation.id,
      email: accountInformation.email,
      apiKey: accountInformation.apiKeys[0].value
    };

    LocalStorageService.storeLogin({apiKey: authenticatedAccount.apiKey});

    dispatch(buildAuthenticationSuccessAction(authenticatedAccount));
    dispatch(SalesDemosStore.loadSalesDemos());
  } catch (e) {
    dispatch(buildAuthenticationErrorAction());
  }
};

export const loginWithLocalStorageApiKey = (): Thunk => async dispatch => {
  dispatch(buildAuthenticationWithApiKeyStartAction());
  const loginData = LocalStorageService.getLogin();

  if (loginData === undefined || loginData.apiKey === undefined) {
    LocalStorageService.removeLogin();
    dispatch(buildAuthenticationWithApiKeyErrorAction());
    return;
  }

  try {
    const accountInformation: AccountInformation = await UserService.loginWithApiKey(loginData.apiKey);

    const authenticatedAccount: AuthenticatedAccount = {
      id: accountInformation.id,
      email: accountInformation.email,
      apiKey: accountInformation.apiKeys[0].value
    };

    dispatch(buildAuthenticationSuccessAction(authenticatedAccount));
    dispatch(SalesDemosStore.loadSalesDemos());
  } catch (e) {
    dispatch(buildAuthenticationWithApiKeyErrorAction());
  }
};

export const logout = (): Thunk => async dispatch => {
  LocalStorageService.removeLogin();
  dispatch(buildAuthenticationLogoutAction());
};

//#endregion

//#region State

export enum AuthenticationStatus {
  Error = 'ERROR',
  LoggedIn = 'LOGGED_IN',
  LoggedOut = 'LOGGED_OUT',
  LoggingIn = 'LOGGING_IN',
  Offline = 'OFFLINE'
}

export interface AuthenticatedAccount {
  readonly id: string;
  readonly email: string;
  readonly apiKey: string;
}

export interface State {
  readonly status: AuthenticationStatus;
  readonly account?: AuthenticatedAccount;
}

const defaultState: State = {
  status: AuthenticationStatus.LoggedOut,
  account: undefined
};

//#endregion

export const reducer = (state: State = defaultState, action: Action): State => {
  switch (action.type) {
    case ActionType.AuthenticationStart:
    case ActionType.AuthenticationWithApiKeyStart:
      return {
        ...state,
        status: AuthenticationStatus.LoggingIn
      };

    case ActionType.AuthenticationSuccess:
      return {
        ...state,
        status: AuthenticationStatus.LoggedIn,
        account: action.authenticatedAccount
      };

    case ActionType.AuthenticationError:
      return {
        ...state,
        status: AuthenticationStatus.Error
      };

    case ActionType.AuthenticationWithApiKeyError:
      return {
        ...state,
        status: AuthenticationStatus.LoggedOut
      };

    case ActionType.AuthenticationLogout:
      return {
        ...state,
        status: AuthenticationStatus.LoggedOut,
        account: undefined
      };

    default:
      return state;
  }
};
